import { alpha, type Theme } from '@mui/material/styles';

/** Visual identity per guided path — keeps review, evidence, and site tours distinct. */
export type GuideTourVariant = 'review-path' | 'evidence-checkpoint' | 'street-view';

export type GuideTourTokens = {
  accent: string;
  accentSoft: string;
  accentBorder: string;
  progressActive: string;
  progressTrack: string;
  spotlightRing: string;
  spotlightGlow: string;
  tipBg: string;
  tipBorder: string;
  scrim: string;
};

export function guideTourTokens(variant: GuideTourVariant, theme: Theme): GuideTourTokens {
  const isLight = theme.palette.mode === 'light';
  const track = alpha(theme.palette.text.primary, isLight ? 0.08 : 0.14);

  if (variant === 'street-view') {
    const accent = isLight ? '#0f766e' : '#2dd4bf';
    return {
      accent,
      accentSoft: alpha(accent, isLight ? 0.07 : 0.14),
      accentBorder: alpha(accent, isLight ? 0.28 : 0.38),
      progressActive: alpha(accent, isLight ? 0.72 : 0.85),
      progressTrack: track,
      spotlightRing: alpha(accent, 0.9),
      spotlightGlow: alpha(accent, 0.16),
      tipBg: alpha('#ca8a04', isLight ? 0.07 : 0.12),
      tipBorder: alpha('#ca8a04', isLight ? 0.22 : 0.3),
      scrim: isLight ? '#f8fafc' : alpha(theme.palette.background.paper, 0.96),
    };
  }

  if (variant === 'evidence-checkpoint') {
    const accent = isLight ? '#b45309' : '#fbbf24';
    return {
      accent,
      accentSoft: alpha(accent, isLight ? 0.08 : 0.14),
      accentBorder: alpha(accent, isLight ? 0.32 : 0.4),
      progressActive: alpha(accent, isLight ? 0.75 : 0.88),
      progressTrack: track,
      spotlightRing: alpha(accent, 0.88),
      spotlightGlow: alpha(accent, 0.14),
      tipBg: alpha(theme.palette.info.main, isLight ? 0.07 : 0.12),
      tipBorder: alpha(theme.palette.info.main, isLight ? 0.2 : 0.28),
      scrim: isLight ? '#fffbf5' : alpha(theme.palette.background.paper, 0.96),
    };
  }

  // review-path — overview / navigation tour
  const accent = isLight ? '#4f46e5' : '#818cf8';
  return {
    accent,
    accentSoft: alpha(accent, isLight ? 0.07 : 0.14),
    accentBorder: alpha(accent, isLight ? 0.26 : 0.36),
    progressActive: alpha(accent, isLight ? 0.7 : 0.82),
    progressTrack: track,
    spotlightRing: alpha(accent, 0.9),
    spotlightGlow: alpha(accent, 0.15),
    tipBg: alpha('#0284c7', isLight ? 0.07 : 0.12),
    tipBorder: alpha('#0284c7', isLight ? 0.2 : 0.28),
    scrim: isLight ? '#fafaff' : alpha(theme.palette.background.paper, 0.96),
  };
}

export function evidenceCheckpointAccent(isRedFlag: boolean, theme: Theme): string {
  return isRedFlag ? theme.palette.error.main : guideTourTokens('evidence-checkpoint', theme).accent;
}

/** Solid elevated panel — glass blur only on floating overlays, not scroll regions. */
export function guidePanelShellSx(tokens: GuideTourTokens, theme: Theme) {
  const isLight = theme.palette.mode === 'light';
  return {
    borderRadius: 2.5,
    border: '1px solid',
    borderColor: tokens.accentBorder,
    bgcolor: tokens.scrim,
    boxShadow: isLight
      ? `0 12px 40px ${alpha(theme.palette.common.black, 0.1)}`
      : `0 12px 40px ${alpha(theme.palette.common.black, 0.45)}`,
  };
}
